import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { InspectionQuestion, InspectionWorkflow, WorkflowStep, IterateStep } from "src/components/pages/configurationDrivenInspections/types/inspectionWorkflow";
import { apiRequest } from "src/utils/apiRequest";

export type WorkflowState = {
    workflows: InspectionWorkflow[];
    activeWorkflow: InspectionWorkflow | null;
    questions: InspectionQuestion[];
    showWorkflowHeader: boolean;
    currentStep: WorkflowStep | IterateStep | null;
    currentIterateStep: WorkflowStep | null;
    shownHumanText: string;
    inspectionId: string;
    currentIterationIndex: number; // 0-based index of current iteration
    totalIterations: number; // Total number of iterations (drum count)
    scheduledWorkflowLoading: boolean; // True while getScheduledWorkflow is in flight
    scheduledWorkflowError: boolean; // True when getScheduledWorkflow failed
};

const initialState: WorkflowState = {
    workflows: [],
    activeWorkflow: null,
    questions: [],
    showWorkflowHeader: true,
    currentStep: null,
    currentIterateStep: null,
    shownHumanText: "",
    inspectionId: '', // Will be set when workflow inspection is created
    currentIterationIndex: 0,
    totalIterations: 0,
    scheduledWorkflowLoading: false,
    scheduledWorkflowError: false,
}

export const getInspectionWorkflow = createAsyncThunk(
    "inspectionWorkflow/getInspectionWorkflow",
    async (_, { rejectWithValue }) => {
        try {
            const data = await apiRequest({url: "/inspections/workflow-config", method: "GET" });
            return data;
        } catch (error: any) {
            return rejectWithValue(error.message || "Failed to fetch inspection workflow");
        }
    }
);

export const getScheduledWorkflow = createAsyncThunk(
    "workflow/getScheduledWorkflow",
    async (scheduledId: string, { rejectWithValue }) => {
        try {
            const data = await apiRequest({ url: `/inspections/scheduled/${scheduledId}/workflow`, method: "GET" });
            return data;
        } catch (error: any) {
            return rejectWithValue(error.message || "Failed to fetch scheduled workflow");
        }
    }
);

export const createWorkflowInspection = createAsyncThunk(
    "workflow/createWorkflowInspection",
    async (
        { templateId, templateName, siteCode, austinInspectionId }: { templateId: string; templateName: string; siteCode?: string; austinInspectionId?: string },
        { rejectWithValue }
    ) => {
        try {
            const data = await apiRequest({
                url: "/inspections",
                method: "POST",
                data: {
                    templateId,
                    templateName,
                    siteCode: siteCode || "ABE2",
                    // Link to the Austin scheduled inspection so results can be
                    // synced back to RCMS when the inspection is submitted.
                    ...(austinInspectionId ? { austinInspectionId } : {}),
                },
            });
            return data;
        } catch (error: any) {
            return rejectWithValue(error.message || "Failed to create workflow inspection");
        }
    }
);

/** Inspection Template Slice */
const { reducer, actions } = createSlice({
  name: "workflow",
  initialState,
  reducers: {
    setShowWorkflowHeader: (state, { payload }) => {
      state.showWorkflowHeader = payload;
    },
    setShownHumanText: (state, { payload }) => {
      state.shownHumanText = payload;
    },
    setCurrentStep: (state, { payload }) => {
      state.currentStep = payload;
    },
    setCurrentIterateStep: (state, { payload }) => {
      state.currentIterateStep = payload;
    },
    setIterationProgress: (state, { payload }: { payload: { currentIndex: number; total: number } }) => {
      state.currentIterationIndex = payload.currentIndex;
      state.totalIterations = payload.total;
    },
    setActiveWorkflow: (state, { payload }: { payload: InspectionWorkflow }) => {
      state.activeWorkflow = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      getInspectionWorkflow.fulfilled,
      (state, { payload }) => {
        state.workflows = payload.workflows ?? [];
        state.questions = payload.questions;
      }
    );
    
    builder.addCase(getScheduledWorkflow.pending, (state) => {
      // Clear any stale workflow so the UI shows a loading state instead of
      // previous/empty data while the request is in flight.
      state.scheduledWorkflowLoading = true;
      state.scheduledWorkflowError = false;
      state.activeWorkflow = null;
      state.questions = [];
    });

    builder.addCase(
      getScheduledWorkflow.fulfilled,
      (state, { payload }) => {
        state.activeWorkflow = payload.workflow;
        state.questions = payload.questions ?? [];
        state.scheduledWorkflowLoading = false;
        state.scheduledWorkflowError = false;
      }
    );

    builder.addCase(getScheduledWorkflow.rejected, (state) => {
      state.scheduledWorkflowLoading = false;
      state.scheduledWorkflowError = true;
    });

    builder.addCase(
      createWorkflowInspection.fulfilled,
      (state, { payload }) => {
        // Set inspectionId from the created inspection response
        state.inspectionId = payload.inspectionId;
      }
    );
  },
});

export const { setShowWorkflowHeader, setShownHumanText, setCurrentStep, setCurrentIterateStep, setIterationProgress, setActiveWorkflow } = actions;

export default reducer;
